import { log } from '../logging/logger';

interface SardiusLoadCustomData {
  preferredAudioLanguage?: string;
  hlsSegmentFormat?: framework.messages.HlsSegmentFormat;
  hlsVideoSegmentFormat?: framework.messages.HlsVideoSegmentFormat;
}

let preferredAudioLanguage: string | null = null;

export function getPreferredAudioLanguage(): string | null {
  return preferredAudioLanguage;
}

export function clearPreferredAudioLanguage(): void {
  preferredAudioLanguage = null;
}

// File extensions that are definitively NOT HLS, even if the sender (incorrectly)
// claims an HLS contentType. The dev sender currently hardcodes an HLS contentType
// for every URL — this guard makes the receiver robust against that and any other
// sender that ships the wrong MIME. Without this we'd try to fetch a 1.7 GB MP4
// as a manifest and hang the receiver.
const NON_HLS_EXTENSIONS = /\.(mp4|mp3|aac|m4a|m4v|mov|wav|ogg|opus|webm|flac)(\?|$)/i;

/**
 * Map a URL extension to the correct MIME type. Used to override a sender's
 * declared contentType when it disagrees with the URL — Cast's internal player
 * router picks the player (MPL vs Shaka) based on contentType, so a sender
 * that claims HLS for a .mp4 URL sends the file through Shaka by mistake.
 */
const URL_EXT_TO_MIME: ReadonlyArray<[RegExp, string]> = [
  [/\.mp4(\?|$)/i, 'video/mp4'],
  [/\.m4v(\?|$)/i, 'video/mp4'],
  [/\.mov(\?|$)/i, 'video/quicktime'],
  [/\.webm(\?|$)/i, 'video/webm'],
  [/\.mp3(\?|$)/i, 'audio/mpeg'],
  [/\.aac(\?|$)/i, 'audio/aac'],
  [/\.m4a(\?|$)/i, 'audio/mp4'],
  [/\.(ogg|opus)(\?|$)/i, 'audio/ogg'],
  [/\.wav(\?|$)/i, 'audio/wav'],
  [/\.flac(\?|$)/i, 'audio/flac'],
];

function mimeFromUrl(url: string): string | null {
  for (const [pattern, mime] of URL_EXT_TO_MIME) {
    if (pattern.test(url)) return mime;
  }
  return null;
}

function looksLikeHls(media: framework.messages.MediaInformation): boolean {
  const url = media.contentUrl ?? media.contentId ?? '';
  if (NON_HLS_EXTENSIONS.test(url)) return false;
  if (/\.m3u8(\?|$)/i.test(url)) return true;
  const ct = (media.contentType ?? '').toLowerCase();
  return (
    ct === 'application/vnd.apple.mpegurl' ||
    ct === 'application/x-mpegurl' ||
    ct === 'audio/mpegurl'
  );
}

interface DetectedFormats {
  hlsSegmentFormat: framework.messages.HlsSegmentFormat;
  hlsVideoSegmentFormat: framework.messages.HlsVideoSegmentFormat;
  source: 'fmp4' | 'ts' | 'aac' | 'fallback';
  isLive: boolean;
  /**
   * The master playlist URL after following redirects. May equal the input
   * URL if there was no redirect. Used by the LOAD interceptor to rewrite
   * request.media.contentId so Cast's player doesn't have to follow the
   * redirect itself — Cast's MPL resolves relative variant URIs against the
   * pre-redirect URL and 400s on shim hosts (e.g. watch.sardius.media) that
   * only redirect the master and not the variants.
   */
  resolvedUrl: string | null;
}

/**
 * Sardius runs at least three HLS encoder pipelines that produce different
 * segment containers:
 *   - multi-audio archives → MPEG-2 TS muxed (.aac/.ts segments, 0x47 sync)
 *   - single-audio archives → fragmented MP4 (CMAF) with EXT-X-MAP init
 *   - live audio-only streams → raw AAC + ID3 timed metadata (.aac segments
 *     starting with 0x49 0x44 0x33 = "ID3" or 0xFF Fx = ADTS sync)
 *
 * Detection strategy:
 *   1. fMP4 — variant playlist contains EXT-X-MAP or .mp4/.m4s URIs
 *   2. TS    — variant playlist contains .ts segment URIs
 *   3. .aac ambiguous case (could be TS-muxed AAC or raw AAC) — peek at the
 *      first 16 bytes of the first segment and check the magic byte
 *   4. Fall back to TS (the Sardius VOD archive default)
 *
 * Also detects live vs VOD from the absence of EXT-X-ENDLIST. Live streams
 * need streamType=LIVE on the LOAD request so Cast picks the right UI and
 * seek behavior.
 *
 * All failure modes (network, CORS, malformed manifest) fall back to TS/VOD.
 */
async function detectHlsSegmentFormats(masterUrl: string): Promise<DetectedFormats> {
  const fallback: DetectedFormats = {
    hlsSegmentFormat: cast.framework.messages.HlsSegmentFormat.TS,
    hlsVideoSegmentFormat: cast.framework.messages.HlsVideoSegmentFormat.MPEG2_TS,
    source: 'fallback',
    isLive: false,
    resolvedUrl: null,
  };

  try {
    const masterResp = await fetch(masterUrl);
    if (!masterResp.ok) return fallback;
    const masterText = await masterResp.text();
    // Use the post-redirect URL as the base for resolving relative variant
    // URIs. Sardius's watch.sardius.media wraps the storage origin via a 302
    // on /live.m3u8 only — variant filenames like "playlist_0.m3u8" don't
    // exist on the watch host. Resolving against masterUrl (pre-redirect)
    // would 400 on those probes; resolving against masterResp.url
    // (post-redirect) lands on storage.sardius.media where they exist.
    const masterBaseUrl = masterResp.url || masterUrl;

    const firstVariantUri = masterText
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line.length > 0 && !line.startsWith('#'));
    if (!firstVariantUri) return fallback;

    const variantUrl = new URL(firstVariantUri, masterBaseUrl).href;
    const variantResp = await fetch(variantUrl);
    if (!variantResp.ok) return fallback;
    const variantText = await variantResp.text();
    const variantBaseUrl = variantResp.url || variantUrl;

    // Live if no EXT-X-ENDLIST. EXT-X-PLAYLIST-TYPE:VOD is also a strong VOD
    // signal, but its absence isn't dispositive (live playlists often omit
    // PLAYLIST-TYPE entirely). ENDLIST is the definitive "this is a complete
    // VOD playlist" marker per RFC 8216.
    const isLive = !/#EXT-X-ENDLIST/i.test(variantText);

    const isFmp4 =
      /#EXT-X-MAP[: ]/i.test(variantText) || /\.(mp4|m4s)(\?|$|\s)/im.test(variantText);
    if (isFmp4) {
      return {
        hlsSegmentFormat: cast.framework.messages.HlsSegmentFormat.FMP4,
        hlsVideoSegmentFormat: cast.framework.messages.HlsVideoSegmentFormat.FMP4,
        source: 'fmp4',
        isLive,
        resolvedUrl: masterBaseUrl,
      };
    }

    const hasDotTs = /\.ts(\?|$|\s)/im.test(variantText);
    const hasDotAac = /\.aac(\?|$|\s)/im.test(variantText);

    // .aac segments are ambiguous: Sardius multi-audio archives mux AAC inside
    // MPEG-TS but still name the files .aac, while live audio-only streams
    // serve raw AAC with an ID3 header. Sniff the first segment's leading
    // bytes to tell them apart. Only spend the extra round trip when the
    // playlist doesn't already declare .ts (which is unambiguous).
    if (hasDotAac && !hasDotTs) {
      const firstSegmentUri = variantText
        .split('\n')
        .map((line) => line.trim())
        .find((line) => line.length > 0 && !line.startsWith('#'));
      if (firstSegmentUri) {
        try {
          const segUrl = new URL(firstSegmentUri, variantBaseUrl).href;
          const segResp = await fetch(segUrl, { headers: { Range: 'bytes=0-15' } });
          if (segResp.ok) {
            const buf = new Uint8Array(await segResp.arrayBuffer());
            // 0x47 = MPEG-TS sync byte
            if (buf.length >= 1 && buf[0] === 0x47) {
              return {
                hlsSegmentFormat: cast.framework.messages.HlsSegmentFormat.TS,
                hlsVideoSegmentFormat: cast.framework.messages.HlsVideoSegmentFormat.MPEG2_TS,
                source: 'ts',
                isLive,
                resolvedUrl: masterBaseUrl,
              };
            }
            // 0x49 0x44 0x33 = "ID3" (Apple HLS timed metadata header), or
            // 0xFF 0xFx = ADTS AAC sync — either way, raw AAC
            const isId3 =
              buf.length >= 3 && buf[0] === 0x49 && buf[1] === 0x44 && buf[2] === 0x33;
            const isAdts =
              buf.length >= 2 && buf[0] === 0xff && (buf[1] ?? 0) >= 0xf0;
            if (isId3 || isAdts) {
              return {
                hlsSegmentFormat: cast.framework.messages.HlsSegmentFormat.AAC,
                hlsVideoSegmentFormat: cast.framework.messages.HlsVideoSegmentFormat.MPEG2_TS,
                source: 'aac',
                isLive,
                resolvedUrl: masterBaseUrl,
              };
            }
          }
        } catch {
          // Segment probe failed — fall through to TS default below.
        }
      }
    }

    return {
      hlsSegmentFormat: cast.framework.messages.HlsSegmentFormat.TS,
      hlsVideoSegmentFormat: cast.framework.messages.HlsVideoSegmentFormat.MPEG2_TS,
      source: 'ts',
      isLive,
      resolvedUrl: masterBaseUrl,
    };
  } catch {
    return fallback;
  }
}

export function installLoadInterceptor(playerManager: framework.PlayerManager): void {
  playerManager.setMessageInterceptor(
    cast.framework.messages.MessageType.LOAD,
    async (request: framework.messages.LoadRequestData) => {
      if (!request.media) {
        log.error('LOAD', 'request missing media');
        const err = new cast.framework.messages.ErrorData(
          cast.framework.messages.ErrorType.LOAD_FAILED,
        );
        err.reason = cast.framework.messages.ErrorReason.INVALID_PARAMS;
        return err;
      }

      const customData = (request.customData ?? {}) as SardiusLoadCustomData;
      preferredAudioLanguage = customData.preferredAudioLanguage ?? null;

      // Fix wrong contentType from misbehaving senders BEFORE running the HLS
      // check. If the URL is unambiguously a non-HLS file (e.g. .mp4) but the
      // sender claims contentType=application/vnd.apple.mpegurl, Cast's player
      // router will send the file through Shaka and silently degrade — log
      // shows PLAYING events but nothing renders. Overwriting contentType to
      // match the URL extension routes it through MPL where it Just Works.
      const url = request.media.contentUrl ?? request.media.contentId ?? '';
      const urlMime = mimeFromUrl(url);
      if (urlMime && request.media.contentType !== urlMime) {
        log.info('LOAD', 'overriding contentType from URL extension', {
          from: request.media.contentType ?? '(empty)',
          to: urlMime,
        });
        request.media.contentType = urlMime;
      }

      const isHls = looksLikeHls(request.media);

      let detectionSource: DetectedFormats['source'] | 'customData' | 'n/a' = 'n/a';
      let detectedLive: boolean | null = null;

      if (isHls) {
        if (customData.hlsSegmentFormat) {
          request.media.hlsSegmentFormat = customData.hlsSegmentFormat;
          request.media.hlsVideoSegmentFormat =
            customData.hlsVideoSegmentFormat ??
            cast.framework.messages.HlsVideoSegmentFormat.MPEG2_TS;
          detectionSource = 'customData';
        } else {
          const detected = await detectHlsSegmentFormats(url);
          request.media.hlsSegmentFormat = detected.hlsSegmentFormat;
          request.media.hlsVideoSegmentFormat = detected.hlsVideoSegmentFormat;
          detectionSource = detected.source;
          detectedLive = detected.isLive;

          // Live streams need streamType=LIVE so Cast picks the right player UI
          // and DVR-aware seek behavior. Senders typically default to BUFFERED
          // because they don't know the manifest content ahead of time.
          if (detected.isLive) {
            request.media.streamType = cast.framework.messages.StreamType.LIVE;
          }

          // Rewrite contentId/contentUrl to the post-redirect URL. Sardius
          // exposes friendly live URLs at watch.sardius.media that return a
          // 302 for the master playlist only — variant filenames don't exist
          // on the watch host. Cast's MPL resolves relative variant URIs
          // against the request's contentId (pre-redirect) and 400s on those
          // shim hosts, so playback fails immediately. Our probe has already
          // followed the redirect to find the real origin; handing that URL
          // back to Cast skips the redirect entirely. No effect on streams
          // that don't redirect.
          if (detected.resolvedUrl && detected.resolvedUrl !== url) {
            log.info('LOAD', 'rewriting contentId to post-redirect URL', {
              from: url,
              to: detected.resolvedUrl,
            });
            if (request.media.contentUrl) request.media.contentUrl = detected.resolvedUrl;
            request.media.contentId = detected.resolvedUrl;
          }
        }
      }

      log.info('LOAD', isHls ? 'HLS request' : 'non-HLS request', {
        url: request.media.contentUrl ?? request.media.contentId ?? '',
        contentType: request.media.contentType ?? '',
        streamType: request.media.streamType ?? '',
        autoplay: request.autoplay ?? true,
        hlsSegmentFormat: request.media.hlsSegmentFormat ?? '',
        hlsVideoSegmentFormat: request.media.hlsVideoSegmentFormat ?? '',
        formatSource: detectionSource,
        isLive: detectedLive === null ? '' : detectedLive,
        preferredAudioLanguage: preferredAudioLanguage ?? '',
        customDataKeys: Object.keys(customData).join(',') || 'none',
      });

      return request;
    },
  );
}
